import { useContext, useEffect, useState } from 'react';
import { AuthContext } from 'context/auth';
import { getSessionUserGroups } from 'context/auth-session';

export default function useApprovalPolling(onApproved?: () => void) {
    const { refreshUser, loading } = useContext(AuthContext);
    const [checking, setChecking] = useState(false);
    const [failed, setFailed] = useState(false);
    const [feedback, setFeedback] = useState('');

    useEffect(() => {
        if (loading) return;
        let active = true;
        let pending = false;
        async function checkApproval() {
            if (!active || pending || document.visibilityState === 'hidden') return;
            pending = true;
            setChecking(true);
            setFailed(false);
            try {
                const user = await refreshUser({ forceRefresh: true });
                if (!active) return;
                if (getSessionUserGroups(user.getSignInUserSession()).includes('flytsuite.default')) {
                    onApproved?.();
                } else {
                    setFeedback('Your account is still awaiting approval. We will check again automatically.');
                }
            } catch {
                if (active) {
                    setFailed(true);
                    setFeedback('Could not check approval. We will retry automatically when your connection is available.');
                }
            } finally {
                pending = false;
                if (active) setChecking(false);
            }
        }
        checkApproval();
        const interval = window.setInterval(checkApproval, 30000);
        window.addEventListener('focus', checkApproval);
        window.addEventListener('online', checkApproval);
        document.addEventListener('visibilitychange', checkApproval);
        return () => {
            active = false;
            window.clearInterval(interval);
            window.removeEventListener('focus', checkApproval);
            window.removeEventListener('online', checkApproval);
            document.removeEventListener('visibilitychange', checkApproval);
        };
    }, [loading, refreshUser, onApproved]);

    return { checking, failed, feedback };
}
